import React, { createContext, useEffect, useState } from "react";
import { NewsApi } from "../Api/NewsApi";
import { News } from "../models/News";
import { OptionType } from "./CaseProvider";

interface NewsProviderProps {
  children: React.ReactNode;
}

interface NewsContextProps {
  news: News[];
  featuredNews: News[];
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  filteredNews: News[];
  newsToDisplay: News[];
  selectedThemes: string[];
  themeTypes: OptionType[];
  formatTypes: OptionType[];
  setSelectedThemes: React.Dispatch<React.SetStateAction<string[]>>;
  selectedFormats: string[];
  setSelectedFormats: React.Dispatch<React.SetStateAction<string[]>>;
  takeAmount: number;
  setTakeAmount: React.Dispatch<React.SetStateAction<number>>;
  skipAmount: number;
  setSkipAmount: React.Dispatch<React.SetStateAction<number>>;
  totalAmount: number;
  selectedDates: Date[];
  setSelectedDates: React.Dispatch<React.SetStateAction<Date[]>>;
  sectorTypes: OptionType[];
  selectedSectors: string[];
  setSelectedSectors: React.Dispatch<React.SetStateAction<string[]>>;
  filtersInUse: boolean;
  setFiltersInUse: React.Dispatch<React.SetStateAction<boolean>>;
  loadingMore: boolean;
  setLoadingMore: React.Dispatch<React.SetStateAction<boolean>>;
}

export const NewsContext = createContext<NewsContextProps>({
  news: [],
  featuredNews: [],
  loading: true,
  setLoading: () => {},
  newsToDisplay: [],
  filteredNews: [],
  selectedThemes: [],
  themeTypes: [],
  formatTypes: [],
  setSelectedThemes: () => {},
  selectedFormats: [],
  setSelectedFormats: () => {},
  takeAmount: 12,
  setTakeAmount: () => {},
  skipAmount: 0,
  setSkipAmount: () => {},
  totalAmount: 0,
  selectedDates: [],
  setSelectedDates: (dates: any) => {},
  sectorTypes: [],
  selectedSectors: [],
  setSelectedSectors: () => {},
  filtersInUse: false,
  setFiltersInUse: () => {},
  loadingMore: false,
  setLoadingMore: () => {},
});

export const NewsProvider: React.FC<NewsProviderProps> = ({ children }) => {
  const [news, setNews] = React.useState<News[]>([]);
  const [featuredNews, setFeaturedNews] = React.useState<News[]>([]);
  const [filteredNews, setFilteredNews] = React.useState<News[]>([]);
  const [newsToDisplay, setNewsToDisplay] = React.useState<News[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [themeTypes, setThemeTypes] = React.useState<OptionType[]>([]);
  const [selectedThemes, setSelectedThemes] = React.useState<string[]>([]);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [formatTypes, setFormatTypes] = React.useState<OptionType[]>([]);
  const [selectedFormats, setSelectedFormats] = React.useState<string[]>([]);
  const [takeAmount, setTakeAmount] = React.useState<number>(12);
  const [skipAmount, setSkipAmount] = React.useState<number>(0);
  const [totalAmount, setTotalAmount] = React.useState<number>(0);
  const [sectorTypes, setSectorTypes] = React.useState<OptionType[]>([]);
  const [selectedSectors, setSelectedSectors] = React.useState<string[]>([]);
  const [filtersInUse, setFiltersInUse] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const filterNewsByStringArray = (
    news: News[],
    selectedTypes: string[],
    newsType: string
  ) => {
    return news.filter((c: News) => {
      if (selectedTypes.length !== 0) {
        return (
          c[newsType] &&
          c[newsType].some((t: string) => selectedTypes.includes(t))
        );
      } else {
        return true;
      }
    });
  };
  const filterNewsByDate = (news: News[], dates: Date[] | null) => {
    if (dates === null || dates.length === 0) {
      return news;
    }

    return news.filter((c: News) => {
      const firstDate = dates[0];
      const lastDate = dates[1];
      // only check on date and not time
      const publishedDate = new Date(c.publishedDate);
      if (firstDate === null) {
        return true;
      }
      publishedDate.setHours(0, 0, 0, 0);
      // if second date is not set, only check if published date is the same or after the first date
      if (lastDate === null || lastDate === undefined) {
        firstDate.setHours(0, 0, 0, 0);
        return c.publishedDate >= firstDate;
      }
      // if both dates are set, check if published date is between the two dates
      if ((firstDate !== null && lastDate !== null) || lastDate !== undefined) {
        lastDate.setHours(0, 0, 0, 0);
        firstDate.setHours(0, 0, 0, 0);

        if (publishedDate >= firstDate && publishedDate <= lastDate) {
          return true;
        }
      }
    });
  };
  const updateAvailableTypes = (news: News[]) => {
    // update sector types and set them to disabled if they are not in the newCases array
    setFormatTypes(() => {
      return formatTypes.map((t) => {
        return {
          label: t.label,
          value: t.value,
          disabled: !news.some((c) => {
            return c.formatTypes.includes(t.value);
          }),
        };
      }).sort((a, b) => {
        // sort the format types alphabetically with the ones that are disabled last
        if (a.disabled && !b.disabled) {
          return 1;
        }
        if (!a.disabled && b.disabled) {
          return -1;
        }
        return a.label.localeCompare(b.label);
      }).sort((a, b) => {
        //sort the selected format types to the top
        if (selectedFormats.includes(a.value) && !selectedFormats.includes(b.value)) {
          return -1;
        }
        if (!selectedFormats.includes(a.value) && selectedFormats.includes(b.value)) {
          return 1;
        }
        return 0;
      });
    });
    // update theme types and set them to disabled if they are not in the newCases array
    setThemeTypes(() => {
      return themeTypes.map((t) => {
        return {
          label: t.label,
          value: t.value,
          disabled: !news.some((c) => {
            return c.themeTypes.includes(t.value);
          }),
        };
      }).sort((a, b) => {
        // sort the theme types alphabetically with the ones that are disabled last
        if (a.disabled && !b.disabled) {
          return 1;
        }
        if (!a.disabled && b.disabled) {
          return -1;
        }
        return a.label.localeCompare(b.label);
      }).sort((a, b) => {
        //sort the selected theme types to the top
        if (selectedThemes.includes(a.value) && !selectedThemes.includes(b.value)) {
          return -1;
        }
        if (!selectedThemes.includes(a.value) && selectedThemes.includes(b.value)) {
          return 1;
        }
        return 0;
      });
    });
    setSectorTypes(() => {
      return sectorTypes.map((t) => {
        return {
          label: t.label,
          value: t.value,
          disabled: !news.some((c) => {
            return c.sectorTypes.includes(t.value);
          }),
        };
      }).sort((a, b) => {
        // sort the sector types alphabetically with the ones that are disabled last
        if (a.disabled && !b.disabled) {
          return 1;
        }
        if (!a.disabled && b.disabled) {
          return -1;
        }
        return a.label.localeCompare(b.label);
      }).sort((a, b) => {
        //sort the selected sector types to the top
        if (selectedSectors.includes(a.value) && !selectedSectors.includes(b.value)) {
          return -1;
        }
        if (!selectedSectors.includes(a.value) && selectedSectors.includes(b.value)) {
          return 1;
        }
        return 0;
      });
    });
  };
  const getFiltersFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const formatTypes = urlParams.getAll("formats");
    const themeTypes = urlParams.getAll("themes");
    const newsDates = urlParams.getAll("dates");
    const sectorTypes = urlParams.getAll("sectors");
    const take = urlParams.get("take");
    const skip = urlParams.get("skip");

    if (
      formatTypes.length === 0 &&
      themeTypes.length === 0 &&
      take === null &&
      skip === null &&
      newsDates.length > 0 &&
      sectorTypes.length === 0
    ) {
      setFiltersInUse(false);
      return;
    }
    if (
      formatTypes.length > 0 ||
      !formatTypes.every((type) => formatTypes.includes(type))
    ) {
      setSelectedFormats(formatTypes);
    }
    if (
      themeTypes.length > 0 ||
      !themeTypes.every((type) => themeTypes.includes(type))
    ) {
      setSelectedThemes(themeTypes);
    }
    if (
      newsDates.length > 0 ||
      !newsDates.every((type) => newsDates.includes(type))
    ) {
      setSelectedDates(newsDates.map((d) => new Date(d)));
    }

    if (take !== null && takeAmount !== parseInt(take)) {
      setTakeAmount(parseInt(take));
    }
    if (skip !== null && skipAmount !== parseInt(skip)) {
      setSkipAmount(parseInt(skip));
    }
    if (
      sectorTypes.length > 0 ||
      !sectorTypes.every((type) => sectorTypes.includes(type))
    ) {
      setSelectedSectors(sectorTypes);
    }
    setFiltersInUse(true);
  };

  const setFiltersInUrl = () => {
    const urlParams = new URLSearchParams();

    if (selectedFormats.length !== 0) {
      selectedFormats.forEach((t) => {
        urlParams.append("formats", t);
      });
    }
    if (selectedThemes.length !== 0) {
      selectedThemes.forEach((t) => {
        urlParams.append("themes", t);
      });
    }

    if (selectedDates !== null && selectedDates.length !== 0) {
      selectedDates.forEach((t) => {
        if (t !== null) {
          urlParams.append("dates", t.toISOString());
        }
      });
    }
    if (selectedSectors.length !== 0) {
      selectedSectors.forEach((t) => {
        urlParams.append("sectors", t);
      });
    }
    urlParams.append("take", takeAmount.toString());
    urlParams.append("skip", skipAmount.toString());
    
    // Preserve the hash when updating the URL
    const hash = window.location.hash;
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${urlParams.toString()}${hash}`
    );
  };
  useEffect(() => {
    NewsApi.getNews().then((data) => {
      const formatTypesList: OptionType[] = [];
      const themeTypesList: OptionType[] = [];
      const sectorTypesList: OptionType[] = [];
      //sort the news by latest first and then slice the first three to featured news
      const newsListAfterThree: News[] = data.items.sort().slice(3) as News[];
      newsListAfterThree.forEach((n) => {
        n.formatTypes.forEach((t: string) => {
          if (!formatTypesList.some((s) => s.value === t)) {
            formatTypesList.push({ label: t, value: t, disabled: false });
          }
        });
        n.themeTypes.forEach((t: string) => {
          if (!themeTypesList.some((s) => s.value === t)) {
            themeTypesList.push({ label: t, value: t, disabled: false });
          }
        });
        n.sectorTypes.forEach((t: string) => {
          if (!sectorTypesList.some((s) => s.value === t)) {
            sectorTypesList.push({ label: t, value: t, disabled: false });
          }
        });
      });
      setNews(data.items as News[]);
      setFormatTypes(formatTypesList.sort((a, b) => a.label.localeCompare(b.label)));
      setThemeTypes(themeTypesList.sort((a, b) => a.label.localeCompare(b.label)));
      setSectorTypes(sectorTypesList.sort((a, b) => a.label.localeCompare(b.label)));
      setTotalAmount(data.total);
    });
  }, []);
  useEffect(() => {
    setNewsToDisplay(() => {
      return filteredNews.slice(skipAmount, skipAmount + takeAmount);
    });
  }, [skipAmount, takeAmount]);
  useEffect(() => {
    if (news.length > 0) {
      getFiltersFromUrl();
      setFilteredNews(news.slice(3) as News[]);
      setFeaturedNews(news.slice(0, 3) as News[]);
    }
  }, [news]);
  useEffect(() => {
    setNewsToDisplay(() => {
      return filteredNews.slice(skipAmount, skipAmount + takeAmount);
    });
    if (
      selectedFormats.length === 0 &&
      selectedThemes.length === 0 &&
      takeAmount === 12 &&
      skipAmount === 0 &&
      selectedDates.length === 0 &&
      selectedSectors.length === 0
    ) {
      setFiltersInUse(false);
    } else {
      setFiltersInUse(true);
    }
    if (filteredNews.length > 0) {
      setTimeout(() => {
        setLoading(false);
        setLoadingMore(false);
      }, 500);
    } else {
      setTimeout(() => {
        setLoading(false);
        setLoadingMore(false);
      }, 500);
    }
  }, [filteredNews]);
  useEffect(() => {
    if (news.length > 0 && filteredNews.length > 0) {
      setFiltersInUrl();
    }
  }, [newsToDisplay]);
  useEffect(() => {
    setFilteredNews(() => {
      let newNewsWithTheme: News[] = [];
      let newNews: News[] = [];
      if (
        selectedFormats.length === 0 &&
        selectedThemes.length === 0 &&
        selectedDates === null &&
        selectedSectors.length === 0
      ) {
        updateAvailableTypes(news.slice(3));
        return news.slice(3).filter(item => !item.hideInCollections);
      }

      // copy cases to newCases array
      newNews = [...news.slice(3)].filter(item => !item.hideInCollections);
      // add the filtered cases to the newCases array if they are not already in the array and are not duplicates and are not empty
      newNews = filterNewsByStringArray(newNews, selectedThemes, "themeTypes");
      newNews = filterNewsByStringArray(
        newNews,
        selectedFormats,
        "formatTypes"
      );
      newNews = filterNewsByStringArray(
        newNews,
        selectedSectors,
        "sectorTypes"
      );
      newNews = filterNewsByDate(newNews, selectedDates);
      newNews = newNews.filter(
        (c, i, a) => a.findIndex((t) => t.iID === c.iID) === i
      );
      updateAvailableTypes(newNews);
      //setSkipAmount(0);
      //setTakeAmount(12);
      return newNews;
    });
    setLoading(true);
    //add all cases that match the selected types and dont remove existing filtered cases only if they are duplicates
  }, [selectedFormats, selectedThemes, selectedSectors, selectedDates]);

  return (
    <NewsContext.Provider
      value={{
        news,
        featuredNews,
        loading,
        setLoading,
        filteredNews,
        newsToDisplay,
        selectedThemes,
        themeTypes,
        formatTypes,
        setSelectedThemes,
        selectedFormats,
        setSelectedFormats,
        takeAmount,
        setTakeAmount,
        skipAmount,
        setSkipAmount,
        totalAmount,
        selectedDates,
        selectedSectors,
        setSelectedSectors,
        sectorTypes,
        setSelectedDates,
        filtersInUse,
        setFiltersInUse,
        loadingMore,
        setLoadingMore,
      }}
    >
      {children}
    </NewsContext.Provider>
  );
};
